import { supabase } from '@/lib/supabase';
import type { Source } from '@/types/database';

// =========================================
// Queries (read operations)
// =========================================

/** Fetch sources for a user (select *) */
export function buildUserSourcesQuery(userId: string) {
  return supabase
    .from('sources')
    .select('*')
    .eq('user_id', userId);
}

/** Fetch a paginated page of folder_sources by folder ID */
export async function fetchFolderSourceIds(
  folderId: string,
  offset: number,
  limit: number,
) {
  return supabase
    .from('folder_sources')
    .select('source_id')
    .eq('folder_id', folderId)
    .range(offset, offset + limit - 1);
}

/** Fetch sources by user ID filtered to a set of IDs */
export function buildUserSourcesByIdsQuery(userId: string, ids: string[]) {
  return supabase
    .from('sources')
    .select('*')
    .eq('user_id', userId)
    .in('id', ids);
}

/** Fetch paginated sources via RPC */
export async function rpcGetFolderSourcesPaginated(params: {
  p_user_id: string;
  p_folder_id: string | null;
  p_search: string | null;
  p_extraction_status: string | null;
  p_author: string | null;
  p_doi: string | null;
  p_journal: string | null;
  p_year_from: number | null;
  p_year_to: number | null;
  p_has_duplicates: boolean | null;
  p_sort_by: string;
  p_sort_order: string;
  p_offset: number;
  p_limit: number;
}) {
  return supabase.rpc('get_folder_sources_paginated', params);
}

/** Fetch a single source by ID scoped to a user */
export async function fetchSourceByIdForUser(sourceId: string, userId: string) {
  return supabase
    .from('sources')
    .select('*')
    .eq('id', sourceId)
    .eq('user_id', userId)
    .single();
}

/** Fetch folder links for a source */
export async function fetchFolderLinksForSource(sourceId: string) {
  return supabase
    .from('folder_sources')
    .select('folder_id')
    .eq('source_id', sourceId);
}

/** Fetch folders by IDs (id, name, color) */
export async function fetchFoldersByIds(ids: string[]) {
  return supabase
    .from('folders')
    .select('id, name, color')
    .in('id', ids);
}

/** Fetch extraction status for a source */
export async function fetchExtractionStatus(sourceId: string) {
  return supabase
    .from('sources')
    .select('id, extraction_status, extracted_text')
    .eq('id', sourceId)
    .single();
}

/** Fetch sources by IDs (id, file_path) for batch delete */
export async function fetchSourceFilePaths(sourceIds: string[]) {
  return supabase
    .from('sources')
    .select('id, file_path')
    .in('id', sourceIds);
}

// =========================================
// Mutations (write operations)
// =========================================

/** Upload a file to the sources storage bucket */
export async function uploadSourceFile(filePath: string, file: File) {
  return supabase.storage
    .from('sources')
    .upload(filePath, file);
}

/** Insert a new source record */
export async function insertSource(params: {
  user_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  extraction_status: string;
  metadata: Record<string, unknown>;
}) {
  return supabase
    .from('sources')
    .insert(params)
    .select()
    .single();
}

/** Insert a folder_source link */
export async function insertFolderSource(folderId: string, sourceId: string) {
  return supabase
    .from('folder_sources')
    .insert({ folder_id: folderId, source_id: sourceId });
}

/** Update a source by ID */
export async function updateSource(
  id: string,
  updates: Partial<Pick<Source, 'title' | 'authors' | 'publication_year' | 'journal' | 'doi' | 'abstract' | 'keywords'>> & { updated_at: string },
) {
  return supabase
    .from('sources')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
}

/** Fetch a source's file_path by ID */
export async function fetchSourceFilePath(sourceId: string) {
  return supabase
    .from('sources')
    .select('file_path')
    .eq('id', sourceId)
    .single();
}

/** Remove files from sources storage bucket */
export async function removeSourceFiles(filePaths: string[]) {
  return supabase.storage.from('sources').remove(filePaths);
}

/** Delete folder_source links by source ID */
export async function deleteFolderSourcesBySourceId(sourceId: string) {
  return supabase
    .from('folder_sources')
    .delete()
    .eq('source_id', sourceId);
}

/** Delete a source by ID */
export async function deleteSource(sourceId: string) {
  return supabase
    .from('sources')
    .delete()
    .eq('id', sourceId);
}

/** Delete a specific folder_source link */
export async function deleteFolderSource(folderId: string, sourceId: string) {
  return supabase
    .from('folder_sources')
    .delete()
    .eq('folder_id', folderId)
    .eq('source_id', sourceId);
}

/** Delete all folder_source links for a source (used before moving) */
export async function deleteAllFolderSourceLinks(sourceId: string) {
  return supabase
    .from('folder_sources')
    .delete()
    .eq('source_id', sourceId);
}

/** Mark a source as duplicate of another */
export async function markSourceAsDuplicate(deleteId: string, keepId: string) {
  return supabase
    .from('sources')
    .update({
      is_duplicate: true,
      duplicate_of: keepId,
      updated_at: new Date().toISOString(),
    })
    .eq('id', deleteId);
}

/** Batch delete folder_source links by source IDs */
export async function batchDeleteFolderSources(sourceIds: string[]) {
  return supabase
    .from('folder_sources')
    .delete()
    .in('source_id', sourceIds);
}

/** Batch delete sources by IDs */
export async function batchDeleteSources(sourceIds: string[]) {
  return supabase
    .from('sources')
    .delete()
    .in('id', sourceIds);
}

/** Batch upsert folder_source links */
export async function batchUpsertFolderSources(
  rows: Array<{ folder_id: string; source_id: string }>,
) {
  return supabase
    .from('folder_sources')
    .upsert(rows, { onConflict: 'folder_id,source_id' });
}
